import * as THREE from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { store } from "../core/store.js";
import { resolutionManager } from "../core/resolution.js";
import { notifications } from "../ui/notifications.js";
import { ScreenLabel } from "./screenLabel.js";
import { WorldLabel } from "./worldLabel.js";
import { StaticTextLabel } from "./StaticTextLabel.js";
import { StaticImageLabel } from "./StaticImageLabel.js";
import { FigureTextLabel } from "./FigureTextLabel.js";
import { FigureImageLabel } from "./FigureImageLabel.js";
import { cleanupGizmo } from "../../../ui/components/Utils/gizmoUtil.js";
import { shouldIgnoreShortcut } from "../../../ui/utils/hotkeys.js";
import { sceneStates } from "../tools/sceneStates.js";
import { DEFAULT_LABEL_FONT } from "./labelFontOptions.js";
import { getTransformControlsModeForShortcut } from "./transformControlShortcuts.js";
import { keepTransformControlsVisible } from "./transformControlsOverlay.js";

var controller = { public: {} };

let listeners = []; // Update Listeners.

let allLabels = [];
let worldLabelsList = []; // List
let worldLabelsUUID = {}; // UID
let labelsOID = {}; // Original ID
let controls = [];
let controlsMode = "translate";
let helperObjects = []; // Dummy 3D objects for ScreenLabels to allow controls to have an object to attach to.
let debugLimits = [];
let activeLabel = {};
let lastGeneratedLabelID = null; // Cache for last ID value.

// Label visibility state tracking
let labelsVisible = {
  screen: false,
  world: false,
  static: false
};

function findID() {
  // Find the largest ID number in use and return 1 greater.
  // Cache the result in lastGeneratedLabelID;
  // The cache will prevent ID reuse when deleting labels
  if (lastGeneratedLabelID === null || isNaN(lastGeneratedLabelID) || labelsOID[lastGeneratedLabelID + 1] !== undefined) {
    let idArray = Object.keys(labelsOID)
      .filter((key) => !isNaN(parseInt(key)))
      .map((key) => parseInt(key))
      .sort((a, b) => a - b);
    if (idArray.length == 0) {
      lastGeneratedLabelID = 1;
    } else {
      lastGeneratedLabelID = idArray[idArray.length - 1] + 1;
    }
  } else {
    console.log("Label from cache");
    lastGeneratedLabelID += 1;
  }
  return lastGeneratedLabelID;
}

controller.checkSelection = function (x, y, action) {
  // Checks for clicks on 3D labels.
  // Currently only used for labels with hyperlinks.

  if (action !== "click") {
    return;
  }
  const tempModelList = [];
  const labelUUIDToURL = {};

  worldLabelsList.map((label, index) => {
    // For each 3D label, store the mesh and UUID of mesh.
    if (Object.prototype.hasOwnProperty.call(label, "url")) {
      if (label.content.url.length > 1) {
        tempModelList.push(label.meshManager.getMesh());
        const backboardUUID = label.meshManager.getMesh().children[0].uuid;
        labelUUIDToURL[backboardUUID] = label.content.url;
      }
    }
  });

  var raycaster = new THREE.Raycaster();
  var mouse = new THREE.Vector2();

  const resolution = resolutionManager.getResolution();
  mouse.x = (x / resolution.width) * 2 - 1;
  mouse.y = -(y / resolution.height) * 2 + 1;
  raycaster.setFromCamera(mouse, store.camera);
  var intersects = raycaster.intersectObjects(tempModelList, true);
  if (intersects.length === 0) return false;
  const intersectUUID = intersects[0].object.uuid;
  if (!Object.prototype.hasOwnProperty.call(labelUUIDToURL, intersectUUID)) return false;
  // Open URL in new tab.
  window.open(labelUUIDToURL[intersectUUID], "_blank");
  store.controls.enabled = false;
  // Need to disable and renable the controls otherwise the click will get stuck due to the tab/window switch.
  window.setTimeout(() => {
    store.controls.enabled = true;
  }, 10);

  return true;
};

controller.createLabel = function (options) {
  if (Object.prototype.hasOwnProperty.call(labelsOID, options.id)) {
    console.warn("Unable to add label. Duplicate ID");
    return;
  }
  var tempLabel = {};
  if (options.type === "2D") {
    tempLabel = new ScreenLabel(options);
    labelsOID[options.id] = tempLabel;
    allLabels.push(tempLabel);
    tempLabel.marker.addClickCallback(() => {
      notifyLabelsUpdated();
    });
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.screen);
  } else if (options.type === "3D") {
    tempLabel = new WorldLabel(options);
    allLabels.push(tempLabel);
    labelsOID[options.id] = tempLabel;
    worldLabelsUUID[tempLabel.uuid] = tempLabel;
    worldLabelsList.push(tempLabel);
    tempLabel.marker.addClickCallback(() => {
      notifyLabelsUpdated();
    });
    if (typeof options.pointerVisible === 'boolean') {
      tempLabel.setPointerVisible(options.pointerVisible);
    }
    if (options.settings && options.settings.pointerThickness) {
      tempLabel.settings.pointerThickness = options.settings.pointerThickness;
      tempLabel.meshManager.setPointerThickness(options.settings.pointerThickness);
    }
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.world);
  } else if (options.type === "STATIC_TEXT") {
    tempLabel = new StaticTextLabel(options);
    tempLabel.id = options.id;
    labelsOID[options.id] = tempLabel;
    allLabels.push(tempLabel);
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.static);
  } else if (options.type === "STATIC_IMAGE") {
    tempLabel = new StaticImageLabel(options);
    tempLabel.id = options.id;
    labelsOID[options.id] = tempLabel;
    allLabels.push(tempLabel);
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.static);
  } else if (options.type === "STATIC_TEXT_LABEL") {
    tempLabel = new FigureTextLabel(options);
    tempLabel.id = options.id;
    labelsOID[options.id] = tempLabel;
    allLabels.push(tempLabel);

    // If it has a parent, add it to the parent's children
    if (options.parentImageId) {
      const parentLabel = labelsOID[options.parentImageId];
      if (parentLabel && parentLabel.type === "STATIC_IMAGE_LABEL") {
        parentLabel.addChildTextLabel(options.id);
      }
    }
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.static);
  } else if (options.type === "STATIC_IMAGE_LABEL") {
    tempLabel = new FigureImageLabel(options);
    tempLabel.id = options.id;
    labelsOID[options.id] = tempLabel;
    allLabels.push(tempLabel);
    // Set initial visibility based on global state
    tempLabel.setVisible(labelsVisible.static);
  } else {
    console.warn("Invalid label type");
  }
};

controller.createTemporaryScreenLabel = function (options) {
  var tempLabel = new ScreenLabel(options);
  return tempLabel;
};

// Static Labels
controller.public.centerStaticLabel = (id) => {
  let label = labelsOID[id];
  if (!label) return;
  if (label.type !== "STATIC_IMAGE" && label.type !== "STATIC_TEXT" && label.type !== "STATIC_IMAGE_LABEL" && label.type !== "STATIC_TEXT_LABEL")
    return;

  label.centerPosition();
  
  if (label.type === "STATIC_IMAGE_LABEL" || label.type === "STATIC_IMAGE") {
    label.childTextLabels.forEach((textId) => {
      const textLabel = labelsOID[textId];
      if (textLabel && textLabel.textPosition) {
        controller.public.positionTextRelativeToImage(textId, id, textLabel.textPosition);
      }
    });
  }
  // repositionTextLabelIfNeeded(id);
  store.requestRender();
  store.setUnsavedScene();
};

// controller.public.setStaticImage = (id, url, onComplete) => {
//   let label = labelsOID[id];
//   if (!label) return;
//   if (label.type !== "STATIC_IMAGE" && label.type !== "STATIC_IMAGE_LABEL") return;
//   fetch(url).then((response) => {
//     if (response.status === 200) {
//       label.loadImage(url);
//       if (typeof onComplete === "function") {
//         console.log(label.imageURL);
//         onComplete();
//       }
//     } else {
//       console.log("Bad URL");
//     }
//   });
// };

controller.public.setStaticImage = (id, url, onComplete) => {
  let label = labelsOID[id];
  if (!label) return;
  if (label.type !== "STATIC_IMAGE" && label.type !== "STATIC_IMAGE_LABEL") return;

  if (!url) {
    if (typeof label.clearImage === "function") {
      label.clearImage();
    } else {
      label.imageURL = "";
      label.imageVisible = true;
      if (label.sprite) {
        label.sprite.visible = false;
      }
    }

    store.requestRender();
    store.setUnsavedScene();
    notifyLabelsUpdated();

    if (typeof onComplete === "function") {
      onComplete();
    }
    return;
  }
  
  fetch(url).then((response) => {
    if (response.status === 200) {
      label.loadImage(url);
      
      setTimeout(() => {
        if ((label.type === "STATIC_IMAGE_LABEL" || label.type === "STATIC_IMAGE") && label.childTextLabels) {
          label.childTextLabels.forEach((textId) => {
            const textLabel = labelsOID[textId];
            if (textLabel && textLabel.textPosition) {
              controller.public.positionTextRelativeToImage(textId, id, textLabel.textPosition);
            }
          });
        }
        
        store.requestRender();
        store.setUnsavedScene();
        notifyLabelsUpdated();
        
        if (typeof onComplete === "function") {
          console.log(label.imageURL);
          onComplete();
        }
      }, 500);
      
    } else {
      console.log("Bad URL");
      if (typeof onComplete === "function") {
        onComplete();
      }
    }
  }).catch((error) => {
    console.error("Error loading image:", error);
    if (typeof onComplete === "function") {
      onComplete();
    }
  });
};

controller.public.setStaticPosition = (id, pos) => {
  let label = labelsOID[id];
  if (!label) return;
  if (
    label.type !== "STATIC_TEXT" &&
    label.type !== "STATIC_IMAGE" &&
    label.type !== "STATIC_TEXT_LABEL" &&
    label.type !== "STATIC_IMAGE_LABEL"
  )
    return;
  if (!pos) return;

  const currentPos = label.getPosition();
  const newX = pos.x !== undefined ? pos.x : currentPos.x;
  const newY = pos.y !== undefined ? pos.y : currentPos.y;

  label.setPosition(newX, newY);

  // If it's a figure image, update child positions
  if (label.type === "STATIC_IMAGE_LABEL" || label.type === "STATIC_IMAGE") {
    label.childTextLabels.forEach((textId) => {
      const textLabel = labelsOID[textId];
      if (textLabel && textLabel.textPosition) {
        controller.public.positionTextRelativeToImage(textId, id, textLabel.textPosition);
      }
    });
  }
  // repositionTextLabelIfNeeded(id);
};

controller.public.setStaticScale = (id, scaleFactor) => {
  let label = labelsOID[id];
  if (!label) return;
  if (label.type !== "STATIC_TEXT" && label.type !== "STATIC_IMAGE" && label.type !== "STATIC_TEXT_LABEL" && label.type !== "STATIC_IMAGE_LABEL")
    return;
  if (isNaN(scaleFactor)) return;
  label.setSize(scaleFactor);

  if (label.type === "STATIC_IMAGE_LABEL" || label.type === "STATIC_IMAGE") {
    label.childTextLabels.forEach((textId) => {
      const textLabel = labelsOID[textId];
      if (textLabel && textLabel.textPosition) {
        controller.public.positionTextRelativeToImage(textId, id, textLabel.textPosition);
      }
    });
  }
  //repositionTextLabelIfNeeded(id);
};

// 2D & 3D Labels

// 2D Only

controller.public.toggle2DSceneStateable = (id) => {
  let label = labelsOID[id];
  if (!label || label.type !== "2D") return;

  label.isSceneStateable = !label.isSceneStateable;
  notifyLabelsUpdated();
};

controller.public.toggleVisibleStart = (id) => {
  let label = labelsOID[id];
  if (!label) return;

  label.visibleStart = !label.visibleStart;
  notifyLabelsUpdated();
};

controller.public.createCaption = () => {
  const options = {
    type: "STATIC_TEXT",
    id: findID(),
  };
  controller.createLabel(options);
  controller.public.toggleVisible(options.id);
  controller.public.editText(options.id, 0);
  const label = labelsOID[options.id];
};

controller.public.debugLabel = (id) => {
  if (!labelsOID[id]) return;
  return labelsOID[id];
};

controller.public.create = (type, createOptions = {}) => {
  if (
    type !== "2D" &&
    type !== "3D" &&
    type !== "STATIC_TEXT" &&
    type !== "STATIC_IMAGE" &&
    type !== "STATIC_TEXT_LABEL" &&
    type !== "STATIC_IMAGE_LABEL"
  ) {
    return;
  }

  let options = {};

  if (type === "2D" || type === "3D") {
    options = {
      anchorPosition: { x: 1, y: 1, z: 1 },
      type: type,
      content: {
        text: [],
        style: {
          backgroundColor: "rgba(33, 33, 33, 1)",
          color: "#FFFFFF",
          fontAlign: "center",
          padding: 0,
        },
      },
      scale: 10,
      offsetX: 0,
      offsetY: 25,
      renderLimits: [],
      id: findID(),
    };

    if (type === "2D") {
      options.isSceneStateable = true;
    }

    if (type === "3D") {
      const textRenderer = createOptions.textRenderer === "canvas" ? "canvas" : "sdf";
      options.offsetX = 3;
      options.offsetY = 3;
      options.content.style.font = `90px ${DEFAULT_LABEL_FONT}`;
      options.content.style.fontSize = 90;
      options.content.style.textRenderer = textRenderer;
    }

    options.content.text.push("Label");
  } else if (type === "STATIC_TEXT" || type === "STATIC_IMAGE" || type === "STATIC_TEXT_LABEL" || type === "STATIC_IMAGE_LABEL") {
    options = {
      id: findID(),
      type: type,
    };
  }

  controller.createLabel(options);
  store.setUnsavedScene();
  return options.id;
};

controller.public.setURL = (id) => {
  const label = labelsOID[id];
  if (!label) return;
  if (label.type !== "3D") return;
  let url = label.content.url || "";
  url = window.prompt("URL\nMust include http(s)://\nLeave blank to disable:", url);
  if (url === null) {
    return;
  }

  label.content.url = url;
  store.setUnsavedScene();
};

controller.public.duplicate = (id) => {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  const serializedData = JSON.parse(JSON.stringify(label.serialize()));
  serializedData.id = findID();

  const hasFigureTextChildren =
    (label.type === "STATIC_IMAGE_LABEL" || label.type === "STATIC_IMAGE") &&
    Array.isArray(label.childTextLabels) &&
    label.childTextLabels.length > 0;

  if (hasFigureTextChildren) {
    const originalChildIds = [...label.childTextLabels];
    serializedData.childTextLabels = [];

    controller.createLabel(serializedData);

    originalChildIds.forEach((childId) => {
      const childLabel = labelsOID[childId];
      if (!childLabel) return;
      if (childLabel.type !== "STATIC_TEXT_LABEL") return;

      const childSerializedData = JSON.parse(JSON.stringify(childLabel.serialize()));
      childSerializedData.id = findID();
      childSerializedData.parentImageId = serializedData.id;

      controller.createLabel(childSerializedData);
    });
  } else {
    controller.createLabel(serializedData);
  }

  store.requestRender();
  store.setUnsavedScene();
};

controller.public.setMarkerOffset = function (id, x, y) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type !== "2D") {
    return;
  }

  if (isNaN(x) || isNaN(y)) {
    return;
  }

  label.setMarkerOffset(x, y);
  store.setUnsavedScene();
};

controller.public.toggleVisible = function (id) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type === "3D") {
    //label.meshManager.pointer.visible = !label.meshManager.pointer.visible;
    //console.log(label);
    label.handleClick();
  } else if (label.type === "2D") {
    label.toggleVisible();
  } else if (
    label.type === "STATIC_TEXT" ||
    label.type === "STATIC_IMAGE" ||
    label.type === "STATIC_TEXT_LABEL" ||
    label.type === "STATIC_IMAGE_LABEL"
  ) {
    const currentVisibility =
      label.type === "STATIC_IMAGE" || label.type === "STATIC_IMAGE_LABEL"
        ? label.labelVisible ?? label.sprite.visible
        : label.sprite.visible;
    const newVisibility = !currentVisibility;

    if ((label.type === "STATIC_IMAGE" || label.type === "STATIC_IMAGE_LABEL") && typeof label.setVisible === "function") {
      label.setVisible(newVisibility);
    } else {
      label.sprite.visible = newVisibility;
    }

    if ((label.type === "STATIC_IMAGE" || label.type === "STATIC_IMAGE_LABEL") && label.childTextLabels && Array.isArray(label.childTextLabels)) {
      label.childTextLabels.forEach((childId) => {
        const childLabel = labelsOID[childId];
        if (childLabel && childLabel.sprite) {
          childLabel.sprite.visible = newVisibility;
        }
      });
    }
  }
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.setStaticImageVisible = function (id, visibleState) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type !== "STATIC_IMAGE" && label.type !== "STATIC_IMAGE_LABEL") {
    return;
  }

  const imageVisible = visibleState !== false;

  if (typeof label.setImageVisible === "function") {
    label.setImageVisible(imageVisible);
  } else {
    label.imageVisible = imageVisible;
    if (label.sprite) {
      label.sprite.visible = imageVisible && (label.labelVisible ?? label.sprite.visible);
    }
  }

  store.requestRender();
  store.setUnsavedScene();
  notifyLabelsUpdated();
};

controller.public.toggleStaticImageVisible = function (id) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  controller.public.setStaticImageVisible(id, label.imageVisible === false);
};

controller.public.scale = function (id, scale) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }
  if (label.type !== "3D") {
    return;
  }

  label.meshManager.setScale(scale);
  if (label.updatePointerRespectingVisibility) {
    label.updatePointerRespectingVisibility();
  } else if (label.meshManager.updatePointer) {
    label.meshManager.updatePointer();
  }
  store.requestRender();
  store.setUnsavedScene();
  notifyLabelsUpdated();
};

controller.public.setWorldLabelImageScale = function (id, imageScale) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type !== "3D" || !label.content?.style?.image) {
    return;
  }

  const nextImageScale = Number(imageScale);
  if (isNaN(nextImageScale) || nextImageScale <= 0) {
    return;
  }

  label.setStyle({ imageScale: nextImageScale });
  store.requestRender();
  store.setUnsavedScene();
  notifyLabelsUpdated();
};

controller.public.setWorldLabelImagePosition = function (id, imagePosition) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type !== "3D" || !label.content?.style?.image) {
    return;
  }

  if (!["below", "above", "left", "right"].includes(imagePosition)) {
    return;
  }

  label.setStyle({ imagePosition });
  store.requestRender();
  store.setUnsavedScene();
  notifyLabelsUpdated();
};

controller.public.zoomTo = function (id) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }
  window.Inventum.camera.zoomToPoint(label.marker.getWorldPosition()); // FIXME Global Reference
};

// Retained for older editor integrations; world labels now have fixed physical size.
controller.public.toggleAutoScale = function () {};

controller.public.toggleAutoRotate = function (id) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type === "2D") {
    return; // Not needed for 2D labels as they always automatically rotate
  }
  label.toggleAutoRotate();
  store.requestRender();
  store.setUnsavedScene();
};

controller.hideAll = function hideAll() {
  allLabels.map((label) => {
    if (label.type === "2D" && (label.isSceneStateable === undefined || !label.isSceneStateable)) return;
    label.setVisible(false);
  });

  if (store.broadcast) {
    store.sync.private.broadcast("LABELS_HIDE_ALL");
  }
  notifyLabelsUpdated();
};

controller.batchSetVisible = function setVisibility(labelArray, isVisible) {
  if (isVisible === undefined) {
    isVisible = true;
  }

  labelArray.map((labelID) => {
    if (labelID === undefined) return;
    const label = controller.getLabelByOriginalID(labelID);
    if (label === undefined || (label.type == "2D" && !label.isSceneStateable)) {
      return;
    }
    label.setVisible(isVisible, true);
  });

  if (store.broadcast) {
    store.sync.private.broadcast("LABELS_SET_VISIBLE", { labels: labelArray, isVisible: isVisible });
  }
  notifyLabelsUpdated();
};

controller.getLabelByOriginalID = function getLabelByOriginalID(id) {
  return labelsOID[id];
};

if (typeof window !== "undefined") {
  window.testLabels = () => {
    return labelsOID;
  };
}

controller.getVisible = function getVisible() {
  const labelsVisible = [];
  allLabels.map((label) => {
    if (label.type === "2D") {
      if (label.isSceneStateable === undefined || !label.isSceneStateable) return;
      if (!label.userHidden) {
        labelsVisible.push(label.id);
      }
    } else if (label.type === "3D") {
      if (label.meshManager.pointer.visible) {
        labelsVisible.push(label.id);
      }
    } else if (
      label.type === "STATIC_TEXT" ||
      label.type === "STATIC_IMAGE" ||
      label.type === "STATIC_TEXT_LABEL" ||
      label.type === "STATIC_IMAGE_LABEL"
    ) {
      if (getLabelVisible(label)) {
        labelsVisible.push(label.id);
      }
    }
  });
  return labelsVisible;
};

function isStaticImageLabel(label) {
  return label && (label.type === "STATIC_IMAGE" || label.type === "STATIC_IMAGE_LABEL");
}

function getLabelVisible(label) {
  if (!label) return false;

  if (isStaticImageLabel(label)) {
    return label.labelVisible ?? label.sprite?.visible ?? false;
  }

  if (label.type === "2D") {
    if (typeof label.isVisible === "function") {
      return label.isVisible();
    }
    return !label.userHidden;
  }

  if (label.type === "3D") {
    return label.meshManager?.pointer?.visible ?? false;
  }

  if (typeof label.isVisible === "function") {
    return label.isVisible();
  }

  return label.sprite?.visible ?? false;
}

controller.render = function renderLabels() {
  worldLabelsList.map((label) => {
    label.handleRotate();
  });
};

controller.reset = function reset() {
  allLabels = [];
  worldLabelsList = []; // List
  worldLabelsUUID = {}; // UID
  labelsOID = {}; // Original ID
  controls = [];
  controlsMode = "translate";
  store.editingLabels = false;
  helperObjects = [];
  lastGeneratedLabelID = null;
  labelsVisible = {
    screen: false,
    world: false,
    static: false
  };
};

function createControls(label) {
  cleanupGizmo(label);
  
  const control = keepTransformControlsVisible(new TransformControls(store.camera, store.webglRenderer.domElement));
  control.addEventListener("dragging-changed", function (event) {
    store.controls.enabled = !event.value;
  });

  if (controls.length === 0) {
    document.addEventListener("keypress", handleControlsMode);
    document.addEventListener("keydown", handleHideControlsShortcut);
  }

  if (label.type === "3D") {
    label.toggleOffsetControls();
    const tMesh = label.meshManager.getMesh();
    control.addEventListener("change", (e) => {
      label.marker.setWorldPosition(tMesh.position);
      label.updateOffsetControls();
      store.requestRender();
    });
    control.attach(tMesh);
  } else if (label.type === "2D") {
    // ScreenLabels needs an Object3D to move around and map to. Then we get the position of that object 3D
    // And pass it to marker.setWorldPosition.
    // As ScreenLabels don't have an Object3D parent this won't work.
    // We should create a sphere or something as a dummy Object3D and then set it's position to the markers world position.
    // Then we can move around and update it

    var tGeom = new THREE.BoxGeometry(1, 1, 1);
    var tMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    var tCube = new THREE.Mesh(tGeom, tMat);
    tCube.visible = false;
    tCube.position.copy(label.marker.getWorldPosition());
    store.scene.add(tCube);
    control.attach(tCube);

    control.addEventListener("change", (e) => {
      label.marker.setWorldPosition(tCube.position);
      store.requestRender();
    });
    helperObjects.push(tCube);
    label._transformControlHelperCube = tCube;
  }
  label._transformControl = control;
  store.scene.add(control);
  controls.push(control);
  store.requestRender();
}

function destroyControls() {
  if (activeLabel && activeLabel.id) {
    cleanupGizmo(activeLabel);
  }

  document.removeEventListener("keypress", handleControlsMode);
  document.removeEventListener("keydown", handleHideControlsShortcut);
  
  controls = [];
  helperObjects = [];
  
  store.requestRender();
}

function hideActiveLabelTransformControls() {
  if (!activeLabel || !activeLabel.id) {
    return false;
  }

  destroyControls();
  activeLabel = {};
  store.editingLabels = false;
  notifyLabelsUpdated();
  return true;
}

function handleHideControlsShortcut(e) {
  if (shouldIgnoreShortcut(e)) {
    return;
  }

  if (e.key === "Escape" || e.code === "Escape" || e.keyCode === 27) {
    if (hideActiveLabelTransformControls()) {
      e.preventDefault();
      e.stopPropagation();
    }
  }
}

function handleControlsMode(e) {
  if (shouldIgnoreShortcut(e)) {
    return;
  }

  controlsMode = getTransformControlsModeForShortcut(e, controlsMode);
  controls.map((control) => {
    control.setMode(controlsMode);
    keepTransformControlsVisible(control);
    if (controlsMode === "rotate") {
      // control.showZ = false;
      control.setRotationSnap(THREE.Math.degToRad(5));
    } else {
      // control.showZ = true;
    }
  });
  store.requestRender();
}

function repositionTextLabelIfNeeded(labelId) {
  const label = labelsOID[labelId];
  if (label && (label.type === "STATIC_TEXT_LABEL" || label.type === "STATIC_TEXT") && label.parentImageId) {
    controller.public.positionTextRelativeToImage(labelId, label.parentImageId, label.textPosition);
  }
}

controller.public.addTextToFigureImage = (imageId, textPosition = "below") => {
  const imageLabel = labelsOID[imageId];

  if (!imageLabel || (imageLabel.type !== "STATIC_IMAGE_LABEL" && imageLabel.type !== "STATIC_IMAGE")) {
    console.warn("Invalid figure image label");
    return;
  }

  let wasUpgraded = false;

  if (imageLabel.type === "STATIC_IMAGE") {
    console.log("Upgrading old-style image label to support text labels");

    if (!imageLabel.childTextLabels) {
      imageLabel.childTextLabels = [];
    }
    if (!imageLabel.defaultTextPosition) {
      imageLabel.defaultTextPosition = "below";
    }
    if (imageLabel.imageVisible === undefined) {
      imageLabel.imageVisible = true;
    }

    if (!imageLabel.addChildTextLabel) {
      imageLabel.addChildTextLabel = function (textLabelId) {
        if (!this.childTextLabels.includes(textLabelId)) {
          this.childTextLabels.push(textLabelId);
        }
      };
    }

    if (!imageLabel.removeChildTextLabel) {
      imageLabel.removeChildTextLabel = function (textLabelId) {
        const index = this.childTextLabels.indexOf(textLabelId);
        if (index > -1) {
          this.childTextLabels.splice(index, 1);
        }
      };
    }

    wasUpgraded = true;
  }

  const textOptions = {
    id: findID(),
    type: "STATIC_TEXT_LABEL",
    parentImageId: imageId,
    textPosition: textPosition,
    content: {
      style: {
        color: "#FFFFFF",
        backgroundColor: "rgba(20, 20, 20, 0.8)",
        padding: 50,
        paddingHeight: 20,
        font: "150px Sans-Serif, Arial",
      },
      text: ["Static Image Text Label"],
    },
  };

  controller.createLabel(textOptions);
  imageLabel.addChildTextLabel(textOptions.id);
  controller.public.toggleVisible(textOptions.id);
  controller.public.positionTextRelativeToImage(textOptions.id, imageId, textPosition);

  if (wasUpgraded) {
    notifyLabelsUpdated();
  }
  store.requestRender();
  store.setUnsavedScene();
  return textOptions.id;
};

controller.public.changeTextPosition = (textId, imageId, newPosition) => {
  const textLabel = labelsOID[textId];
  if (!textLabel || textLabel.type !== "STATIC_TEXT_LABEL") return;
  textLabel.textPosition = newPosition;

  // Reposition the text relative to the image with a small delay to ensure dimensions are current
  setTimeout(() => {
    controller.public.positionTextRelativeToImage(textId, imageId, newPosition);
  }, 50);

  store.setUnsavedScene();
};

controller.public.positionTextRelativeToImage = (textId, imageId, position = "below") => {
  const textLabel = labelsOID[textId];
  const imageLabel = labelsOID[imageId];
  if (!textLabel || !imageLabel) return;

  const imagePos = imageLabel.getPosition();
  const imageSize = imageLabel.getSize();
  const textSize = textLabel.getSize();
  const centerX = imagePos.x + imageSize.x / 2 - textSize.x / 2;
  const gap = 0.02; 
  let newY;
  if (position === "above") {
    newY = imagePos.y - textSize.y - gap;
  } else {
    newY = imagePos.y + imageSize.y + gap;
  }

  textLabel.setPosition(centerX, newY);
  store.requestRender();
};

controller.public.changeTextPosition = (textId, imageId, newPosition) => {
  const textLabel = labelsOID[textId];
  if (!textLabel || textLabel.type !== "STATIC_TEXT_LABEL") return;

  textLabel.textPosition = newPosition;

  controller.public.positionTextRelativeToImage(textId, imageId, newPosition);

  store.setUnsavedScene();
};

controller.public.setFigureTextPosition = (imageId, position) => {
  const imageLabel = labelsOID[imageId];
  if (!imageLabel || imageLabel.type !== "STATIC_IMAGE_LABEL") return;

  imageLabel.defaultTextPosition = position;

  imageLabel.childTextLabels.forEach((textId) => {
    controller.public.positionTextRelativeToImage(textId, imageId, position);
  });

  store.setUnsavedScene();
};

controller.public.getFigureImageChildren = (imageId) => {
  const imageLabel = labelsOID[imageId];
  if (!imageLabel || imageLabel.type !== "STATIC_IMAGE_LABEL" ) return [];

  return imageLabel.childTextLabels.map((textId) => labelsOID[textId]).filter((label) => label);
};

controller.public.getLabelIDS = () => {
  return Object.keys(labelsOID);
};

controller.public.locationControl = (id) => {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    console.log(id);
    console.log(labelsOID);
    return;
  }

  if (label === activeLabel) {
    hideActiveLabelTransformControls();
    return;
  }
  store.editingLabels = true;
  destroyControls();
  activeLabel = label;
  createControls(label);
  store.setUnsavedScene();
};

controller.public.hideTransformControls = hideActiveLabelTransformControls;

controller.public.addLimit = (id, limit) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }
  const newLimit = {};

  if (limit === "ABOVE_ALTITUDE" || limit === "BELOW_ALTITUDE") {
    if (label.renderLimits.length > 0) {
      let canAdd = true;
      label.renderLimits.map((existingLimit) => {
        if (
          existingLimit.type === "ABOVE_ALTITUDE" ||
          existingLimit.type === "BELOW_ALTITUDE" ||
          existingLimit.type === "DISTANCE" ||
          existingLimit.type === "ALTITUDE"
        ) {
          canAdd = false;
        }
      });
      if (!canAdd) {
        notifications.add({ content: "Only one ALTITUDE or DISTANCE limit can exist" });
        return;
      }
    }
    newLimit.value = 40; // Default value for Altitude
  } else if (limit === "DISTANCE") {
    if (label.renderLimits.length > 0) {
      let canAdd = true;
      label.renderLimits.map((existingLimit) => {
        if (
          existingLimit.type === "ABOVE_ALTITUDE" ||
          existingLimit.type === "BELOW_ALTITUDE" ||
          existingLimit.type === "DISTANCE" ||
          existingLimit.type === "ALTITUDE"
        ) {
          canAdd = false;
        }
      });
      if (!canAdd) {
        notifications.add({ content: "Only one ALTITUDE or DISTANCE limit can exist" });
        return;
      }
    }
    newLimit.value = 40;
  } else if (limit === "EXCLUSION" || limit === "INCLUSION") {
    if (label.renderLimits.length > 0) {
      let canAdd = true;
      label.renderLimits.map((existingLimit) => {
        if (
          (existingLimit.type === "EXCLUSION" && limit === "INCLUSION") ||
          (existingLimit.type === "INCLUSION" && limit === "EXCLUSION")
        ) {
          canAdd = false;
        }
      });
      if (!canAdd) {
        notifications.add({ content: "Can't mix Inclusion and Exclusion limits" });
        return;
      }
    }
    newLimit.value = window.Inventum.camera.getCameraPositionWorld(); // Default position value for Exclusion or Inclusion
    newLimit.value.d = 5;
  }

  newLimit.type = limit; // Set the type to

  label.addRenderLimit(newLimit);
  store.requestRender();
  store.setUnsavedScene();
};

controller.public.showLimitBounds = (params) => {
  const tSphereGeom = new THREE.SphereGeometry(params.d, 32, 32);
  const tMat = new THREE.MeshLambertMaterial({ color: 0xff0000, opacity: 0.5, transparent: true });
  const tSphere = new THREE.Mesh(tSphereGeom, tMat);
  tSphere.position.set(params.x, params.y, params.z);
  store.scene.add(tSphere);
  debugLimits.push(tSphere);
  store.requestRender();
};

controller.public.clearLimitBounds = () => {
  debugLimits.map((tSphere) => {
    store.scene.remove(tSphere);
  });
  debugLimits = [];
  store.requestRender();
};

controller.public.updateRenderLimit = (labelID, limitID, value) => {
  const label = labelsOID[labelID];
  if (!label) {
    return;
  }

  if (!label.renderLimits[limitID]) {
    return;
  }

  label.updateRenderLimit(limitID, value);
  notifications.add({ content: "Limit Updated!" });
  store.requestRender();
  store.setUnsavedScene();
};

controller.public.getRenderLimits = (id) => {
  const label = labelsOID[id];
  if (!label) {
    return [];
  }
  var tLimits = [];
  label.renderLimits.map((limit) => {
    tLimits.push(Object.assign({}, limit));
  });
  return tLimits;
};

controller.public.deleteRenderLimit = (labelID, limitID) => {
  const label = labelsOID[labelID];
  if (!label) {
    return;
  }
  label.deleteRenderLimit(limitID);
};

controller.public.delete = (id) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }

  // Handle deletion of image labels with child text labels
  if (label.type === "STATIC_IMAGE_LABEL" || (label.type === "STATIC_IMAGE" && label.childTextLabels)) {
    // Create a copy of the childTextLabels array to avoid modification during iteration
    const childrenToDelete = [...(label.childTextLabels || [])];

    // Also find any orphaned text labels that might reference this image as parent
    const orphanedChildren = allLabels
      .filter((l) => l.type === "STATIC_TEXT_LABEL" && l.parentImageId === id)
      .map((l) => l.id)
      .filter((childId) => !childrenToDelete.includes(childId));

    // Combine both arrays
    const allChildrenToDelete = [...childrenToDelete, ...orphanedChildren];

    // Delete all children first (without recursive parent cleanup to avoid conflicts)
    allChildrenToDelete.forEach((childId) => {
      const childLabel = labelsOID[childId];
      if (childLabel) {
        // Clean up any gizmo controls before removing
        cleanupGizmo(childLabel);
        
        // Remove child directly without calling the full delete function
        // to avoid recursive parent-child cleanup conflicts
        childLabel.remove();
        delete labelsOID[childId];

        // Remove from allLabels array
        const childIndex = allLabels.findIndex((l) => l.id === childId);
        if (childIndex > -1) {
          allLabels.splice(childIndex, 1);
        }
      }
    });

    // Clear the childTextLabels array
    if (label.childTextLabels) {
      label.childTextLabels = [];
    }
  }
  // Handle deletion of text labels (remove from parent)
  else if (label.type === "STATIC_TEXT_LABEL" && label.parentImageId) {
    const parentLabel = labelsOID[label.parentImageId];
    if (parentLabel && parentLabel.removeChildTextLabel) {
      parentLabel.removeChildTextLabel(id);
    }
  }

  cleanupGizmo(label);
  
  // Remove the main label
  label.remove();
  delete labelsOID[id];

  // Remove from allLabels array
  const labelIndex = allLabels.findIndex((l) => l.id === id);
  if (labelIndex > -1) {
    allLabels.splice(labelIndex, 1);
  }

  notifyLabelsUpdated();
  store.requestRender();
  store.setUnsavedScene();
};

controller.public.getLabels = () => {
  const cleanedData = [];
  allLabels.map((label) => {
    let badLabel = false;

    const labelData = {
      id: label.id,
      type: label.type,
    };

    // Render Limits
    if (label.type === "2D" || label.type === "3D") {
      labelData.text = label.content.text.slice();
      if (label.content.richText) {
        labelData.richText = JSON.parse(JSON.stringify(label.content.richText));
      }
      labelData.style = Object.assign({}, label.content.style);
      labelData.marker = {};
      labelData.visibleStart = label.visibleStart;
      labelData.transformActive = label === activeLabel ? true : false;
      const renderLimits = [];
      if (Object.prototype.hasOwnProperty.call(label, "renderLimits")) {
        label.renderLimits.map((limit) => {
          renderLimits.push(Object.assign({}, limit));
        });
      }
      labelData.renderLimits = renderLimits;
      if (label.type === "3D") {
        labelData.scale = label.meshManager.getScale();
        labelData.autoRotate = label.settings.autoRotate;
        labelData.autoScale = label.settings.autoScale;
        labelData.marker.hoverSize = label.settings.markerHoverSize;
        labelData.marker.hoverBackground = label.settings.markerHoverBackground;
        labelData.marker.inactiveSize = label.settings.markerInactiveSize;
        labelData.marker.inactiveBackground = label.settings.markerInactiveBackground;
        labelData.marker.showMarker = label.settings.showMarker;
        labelData.visible = !label.userHidden;
        labelData.settings = {
          pointerThickness: label.settings.pointerThickness || "medium",
        };
        labelData.pointerVisible = label.pointerIntendedVisible;
      } else if (label.type === "2D") {
        labelData.marker.hoverSize = label.markerHoverSize;
        labelData.marker.hoverBackground = label.markerHoverBackground;
        labelData.marker.inactiveSize = label.markerInactiveSize;
        labelData.marker.inactiveBackground = label.markerInactiveBackground;
        labelData.marker.showMarker = label.showMarker;
        labelData.marker.offsetX = label.offsetX;
        labelData.marker.offsetY = label.offsetY;
        labelData.isSceneStateable = label.isSceneStateable;
        labelData.visible = !label.userHidden;
      }
    } else if (
      label.type === "STATIC_IMAGE" ||
      label.type === "STATIC_TEXT" ||
      label.type === "STATIC_IMAGE_LABEL" ||
      label.type === "STATIC_TEXT_LABEL"
    ) {
      labelData.scale = label.scaleFactor;
      labelData.positionX = label.screenPosition.x;
      labelData.positionY = label.screenPosition.y;
      labelData.visible =
        label.type === "STATIC_IMAGE" || label.type === "STATIC_IMAGE_LABEL"
          ? label.labelVisible ?? label.sprite.visible
          : label.sprite.visible;
      labelData.visibleStart = label.visibleStart;
      if (label.type === "STATIC_TEXT" || label.type === "STATIC_TEXT_LABEL") {
        labelData.text = label.getText();
        const richText = label.getRichText ? label.getRichText() : undefined;
        if (richText) {
          labelData.richText = richText;
        }
        labelData.style = label.getStyle();
        if (label.type === "STATIC_TEXT_LABEL") {
          labelData.parentImageId = label.parentImageId;
          labelData.textPosition = label.textPosition;
        }
      } else {
        // Static Image or Figure Image
        labelData.imageURL = label.imageURL;
        labelData.imageVisible = label.imageVisible !== false;
        if (label.type === "STATIC_IMAGE_LABEL" || (label.type === "STATIC_IMAGE" && label.childTextLabels)) {
          labelData.childTextLabels = label.childTextLabels || [];
          labelData.defaultTextPosition = label.defaultTextPosition || "below";
        }
      }
    } else {
      console.log("Unsupported Label Type");
      badLabel = true;
    }

    if (!badLabel) {
      cleanedData.push(labelData);
    }
  });
  return cleanedData;
};

controller.public.setLabelStyle = (id, style) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }

  if (style.padding) {
    style.padding = parseInt(style.padding);
  }

  // Style properties that affect text dimensions/layout
  const dimensionAffectingStyles = [
    "font",
    "padding",
    "paddingHeight",
    "lineSpacing",
    "borderRadius",
    "fontSize",
    "fontFamily",
    "fontWeight",
    "outlineWidth",
    "sdfOutlineWidth",
    "strokeWidth",
    "sdfStrokeWidth",
    "sdfGlyphSize",
    "imageScale",
    "imagePosition",
  ];

  // Check if any of the style changes affect text dimensions
  const shouldRepositionText = dimensionAffectingStyles.some((prop) => style[prop] !== undefined);

  if (label.type === "STATIC_TEXT_LABEL" || label.type === "STATIC_TEXT") {
    label.style = Object.assign({}, label.style, style);

    if (label.canvasTexture && label.canvasTexture.setStyle) {
      label.canvasTexture.setStyle(label.style);
    }

    if (label.setStyle && typeof label.setStyle === "function") {
      label.setStyle(style);
    }
  } else {
    label.setStyle(style);
  }

  // if (shouldRepositionText) {
  //   repositionTextLabelIfNeeded(id);
  // }

  store.requestRender();
  store.setUnsavedScene();
};

controller.public.setMarkerStyle = (id, style) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }
  label.setMarkerStyle(style);
  if (label.type === "2D") {
    label.setMarkerOffset(style.offsetX, style.offsetY);
  }
  store.setUnsavedScene();
};

controller.public.addText = (id, text) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }

  if (text === undefined) {
    text = window.prompt("Text Line:", "Your Text");
    if (text === null || text === "") {
      return;
    }
  }

  label.addText(text);
  store.requestRender();
  store.setUnsavedScene();
};

controller.public.replaceText = (id, textArray, richTextModel) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }

  if (label.type !== "2D" && label.type !== "3D" && label.type !== "STATIC_TEXT" && label.type !== "STATIC_TEXT_LABEL") {
    return;
  }

  const nextText = Array.isArray(textArray) ? textArray : [];

  if (label.type === "2D" || label.type === "3D") {
    label.content.text = nextText;
    if (richTextModel) {
      label.content.richText = JSON.parse(JSON.stringify(richTextModel));
    } else {
      delete label.content.richText;
    }
  }

  if (label.replaceText && typeof label.replaceText === "function") {
    label.replaceText(nextText, richTextModel);
  } else if (label.type === "2D" && label.backboard?.replaceText) {
    label.backboard.replaceText(nextText, richTextModel);
  } else if (label.canvasTexture?.replaceText) {
    label.canvasTexture.replaceText(nextText, richTextModel);
  }

  store.requestRender();
  store.setUnsavedScene();
};

controller.public.editText = (id, index, textValue) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }
  if (label.type !== "2D" && label.type !== "3D" && label.type !== "STATIC_TEXT" && label.type !== "STATIC_TEXT_LABEL") {
    return;
  }
  if (textValue === undefined) {
    let oldText = "";

    if (label.type === "2D" || label.type === "3D") {
      oldText = label.content.text[index];
    } else if (label.type === "STATIC_TEXT" || label.type === "STATIC_TEXT_LABEL") {
      oldText = label.canvasTexture.textArray[index];
    }

    textValue = window.prompt("Text Line:", oldText);

    if (textValue === null || textValue === "") {
      return;
    }
  }
  label.editText(index, textValue);
  //repositionTextLabelIfNeeded(id);
  store.requestRender();
  store.setUnsavedScene()
};

controller.public.clearTextLine = (id, index) => {
  let textArr = null;
  const label = labelsOID[id];
  if (!label) {
    return;
  }

  if (label.type !== "2D" && label.type !== "3D" && label.type !== "STATIC_TEXT" && label.type !== "STATIC_TEXT_LABEL") {
    return;
  }

  if (label.type === "2D" || label.type === "3D") {
    textArr = label.content.text;
  } else if (label.type === "STATIC_TEXT" || label.type === "STATIC_TEXT_LABEL") {
    textArr = label.canvasTexture.textArray;
  }

  if (textArr.length === 1) {
    label.editText(0, "Untitled Label"); // If removing only text item rename it to untitled label
  } else {
    textArr.splice(index, 1);
    label.editText(0, textArr[0]); // Force label to update
  }

  store.requestRender();
  store.setUnsavedScene();
};

controller.public.clearText = (id) => {
  const label = labelsOID[id];
  if (!label) {
    return;
  }
  const emptyArray = [];
  label.content.text = emptyArray;
  label.backboard.replaceText(emptyArray);
  store.requestRender();
  store.setUnsavedScene();
};

controller.modifyScale = (scale) => {
  allLabels.map((label) => {
    if (
      label.type === "STATIC_TEXT" ||
      label.type === "STATIC_IMAGE" ||
      label.type === "STATIC_TEXT_LABEL" ||
      label.type === "STATIC_IMAGE_LABEL" ||
      !label.marker
    ) {
      return;
    }

    label.marker.modifyScale(scale);
    // if label is 3D then modify the 3D positon too
    if (label.type === "3D") {
      label.modifyScale(scale);
    }
  });
  store.setUnsavedScene();
};

controller.modifyTransform = (deltaTransform) => {
  allLabels.map((label) => {
    if (
      label.type === "STATIC_TEXT" ||
      label.type === "STATIC_IMAGE" ||
      label.type === "STATIC_TEXT_LABEL" ||
      label.type === "STATIC_IMAGE_LABEL" ||
      !label.marker
    ) {
      return;
    }

    label.marker.modifyTransform(deltaTransform);
    // if label is 3D then modify the 3D positon too
    if (label.type === "3D") {
      label.modifyTransform(deltaTransform);
    }
  });
  store.setUnsavedScene();
};

function notifyLabelsUpdated() {
  for (const key in listeners) {
    const cb = listeners[key];
    if (typeof cb === "function") {
      cb();
    }
  }
}

controller.public.addUpdateListener = (cb) => {
  if (typeof cb !== "function") return;
  let key = 0;
  while (listeners[key] !== undefined) {
    key++;
  }
  listeners[key] = cb;
  return key;
};

controller.public.removeUpdateListener = (key) => {
  if (listeners[key]) {
    delete listeners[key];
  }
};

controller.setVisibleStartFromCurrentState = function setVisibleStartFromCurrentState() {
  allLabels.map((label) => {
    if (getLabelVisible(label)) {
      label.visibleStart = true;
    } else {
      label.visibleStart = false;
    }
  });
};

controller.showVisibleStart = function showVisibleStart() {
  allLabels.map((label) => {
    if (label.visibleStart) {
      label.setVisible(true);
    } else {
      label.setVisible(false);
    }
  });
};

controller.public.getLabelName = function (id) {
  const label = labelsOID[id];
  return label ? label.name : null;
};

controller.public.updateLabelName = function (id, newName) {
  const label = labelsOID[id];
  if (label) {
    label.name = newName;
    notifyLabelsUpdated();
  } else {
    console.warn(`Label with ID ${id} not found.`);
  }
};

controller.generateJSON = () => {
  const serializedLabels = [];
  allLabels.map((label) => {
    serializedLabels.push(label.serialize());
  });
  return { labels: serializedLabels };
};

controller.public.setPointerVisible = function (id, isVisible) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  label.setPointerVisible(isVisible);
  store.requestRender();
  store.setUnsavedScene();
  notifyLabelsUpdated();
};

controller.public.setPointerThickness = function (id, thickness) {
  const label = labelsOID[id];
  if (!label) {
    console.log("Bad label ID");
    return;
  }

  if (label.type !== "3D") {
    return;
  }

  label.settings.pointerThickness = thickness;
  label.meshManager.setPointerThickness(thickness);
  store.requestRender();
  store.setUnsavedScene();
};

// Label visibility toggle functions
controller.public.toggleScreenLabels = function () {
  labelsVisible.screen = !labelsVisible.screen;
  allLabels.forEach((label) => {
    if (label.type === "2D") {
      label.setVisible(labelsVisible.screen);
    }
  });
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.toggleWorldLabels = function () {
  labelsVisible.world = !labelsVisible.world;
  allLabels.forEach((label) => {
    if (label.type === "3D") {
      label.setVisible(labelsVisible.world);
    }
  });
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.toggleStaticLabels = function () {
  labelsVisible.static = !labelsVisible.static;
  allLabels.forEach((label) => {
    if (
      label.type === "STATIC_TEXT" ||
      label.type === "STATIC_IMAGE" ||
      label.type === "STATIC_TEXT_LABEL" ||
      label.type === "STATIC_IMAGE_LABEL"
    ) {
      label.setVisible(labelsVisible.static);
    }
  });
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.toggleAllLabels = function () {
  // Get the current active scene state
  const currentSceneState = sceneStates.public.getCurrentActive();
  
  if (currentSceneState) {
    // Scene-specific toggle: only toggle labels in the current scene state
    const newState = !currentSceneState.labelsToggleState;
    currentSceneState.labelsToggleState = newState;
    
    // Only toggle labels that belong to this scene state
    controller.batchSetVisible(currentSceneState.labels, newState);
    
    // Mark scene as unsaved
    store.setUnsavedScene();
  } else {
    // Fallback to global behavior when no scene state is active
    const anyVisible = labelsVisible.screen || labelsVisible.world || labelsVisible.static;
    const newState = !anyVisible;
    
    labelsVisible.screen = newState;
    labelsVisible.world = newState;
    labelsVisible.static = newState;
    
    allLabels.forEach((label) => {
      label.setVisible(newState);
    });
  }
  
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.hideAllLabels = function () {
  labelsVisible.screen = false;
  labelsVisible.world = false;
  labelsVisible.static = false;
  
  allLabels.forEach((label) => {
    label.setVisible(false);
  });
  
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.toggleAllLabelsGlobal = function () {
  // Global toggle: toggles labels across ALL scene states
  const currentSceneState = sceneStates.public.getCurrentActive();
  
  // Check if any labels are currently visible
  const anyVisible = allLabels.some(label => {
    if (label.type === "2D") {
      return !label.userHidden;
    } else if (label.type === "3D") {
      return label.meshManager.pointer.visible;
    } else if (label.type === "STATIC_TEXT" || label.type === "STATIC_IMAGE" || 
               label.type === "STATIC_TEXT_LABEL" || label.type === "STATIC_IMAGE_LABEL") {
      return getLabelVisible(label);
    }
    return false;
  });
  
  if (anyVisible) {
    // Turn OFF: Hide all labels across all scene states
    allLabels.forEach((label) => {
      label.setVisible(false);
    });
    
    // Mark all scene states as having labels off
    const allSceneStates = sceneStates.public.getAllSceneStates();
    allSceneStates.forEach((sceneState) => {
      sceneState.labelsToggleState = false;
    });
    
    labelsVisible.screen = false;
    labelsVisible.world = false;
    labelsVisible.static = false;
  } else {
    // Turn ON: Re-enable labels for all scene states and show current scene state's labels
    
    // Mark all scene states as having labels on
    const allSceneStates = sceneStates.public.getAllSceneStates();
    allSceneStates.forEach((sceneState) => {
      sceneState.labelsToggleState = true;
    });
    
    // Show only labels from the current scene state
    if (currentSceneState && currentSceneState.labels) {
      controller.batchSetVisible(currentSceneState.labels, true);
      
      // Update visibility state based on what was shown
      labelsVisible.screen = true;
      labelsVisible.world = true;
      labelsVisible.static = true;
    }
  }
  
  // Mark scene as unsaved and trigger re-render
  store.setUnsavedScene();
  store.requestRender();
  notifyLabelsUpdated();
};

controller.public.getLabelsVisible = function () {
  // Get the current active scene state
  const currentSceneState = sceneStates.public.getCurrentActive();
  
  if (currentSceneState) {
    // When a scene state is active, return its toggle state for the "all" property
    return {
      screen: labelsVisible.screen,
      world: labelsVisible.world,
      static: labelsVisible.static,
      all: currentSceneState.labelsToggleState
    };
  } else {
    // Fallback to global behavior when no scene state is active
    const all = labelsVisible.screen && labelsVisible.world && labelsVisible.static;
    return {
      screen: labelsVisible.screen,
      world: labelsVisible.world,
      static: labelsVisible.static,
      all: all
    };
  }
};

export { controller as labels };
